import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import OTPInput from '../components/OTPInput';
import { 
  ChatBubbleBottomCenterTextIcon, 
  EnvelopeIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const { isAuthenticated, loading: authLoading, setAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // OTP state
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.sendMagicLink(email);
      if (response.data.success) {
        setEmailSent(true);
        // OTP is now automatically generated with the magic link
      } else {
        setError(response.data.error || 'Failed to send secure link');
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtpCode(value);
    setOtpError('');
  };

  const handleOtpComplete = async (code) => {
    if (code.length !== 6) return;
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await api.verifyOTP(email, code, 'login');
      
      if (response.data.success) {
        // Update auth context with user data
        setAuthenticatedUser(response.data.user, response.data.organization);
        
        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Invalid OTP code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || resendLoading) return;
    
    setResendLoading(true);
    setOtpError('');
    
    try {
      const response = await api.resendOTP(email, 'login');
      
      if (response.data.success) {
        setOtpCode('');
        setOtpError('');
        // Start resend countdown
        setCanResend(false);
        setResendCountdown(120); // 2 minutes
      } else {
        setOtpError(response.data.error || 'Failed to resend OTP');
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendCountdown, canResend]);

  // const fillDemoCredentials = () => {
  //   setEmail('support@snwolley.com');
  //   setPassword('AlphanumericAndSpace#');
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex items-center justify-center h-16 w-16 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-2xl shadow-lg">
              <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {emailSent ? 'Check Your Email' : 'Welcome to Snwolley Business API'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {emailSent 
              ? 'We\'ve sent you a secure sign-in link' 
              : 'Enter your email to receive a secure sign-in link'
            }
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {emailSent ? (
            /* Success Message with OTP Option */
            <div className="text-center space-y-6">
              {!showOtpForm ? (
                /* Show Options */
                <>
                  <div className="flex justify-center">
                    <div className="flex items-center justify-center h-16 w-16 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">Login Link Sent!</h3>
                    <p className="text-sm text-gray-600">
                      We've sent verification options to <strong>{email}</strong>
                    </p>
                    <p className="text-xs text-gray-500">
                      The link will expire in 15 minutes for security reasons.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium">Check your email</p>
                          <p className="mt-1">Click the "Sign In Securely" button in the email to access your dashboard.</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-3">Or use the 6-digit code from your email</p>
                      <button
                        onClick={() => setShowOtpForm(true)}
                        disabled={otpLoading}
                        className="inline-flex items-center px-6 py-3 border border-whatsapp-500 text-sm font-medium rounded-lg text-whatsapp-600 bg-whatsapp-50 hover:bg-whatsapp-100 focus:outline-none focus:ring-2 focus:ring-whatsapp-500 transition-all duration-200"
                      >
                        <ArrowRightIcon className="h-4 w-4 mr-2" />
                        Enter OTP Code
                      </button>
                    </div>

                    <button
                      onClick={() => {setEmailSent(false); setEmail(''); setError(''); setOtpCode(''); setOtpError('');}}
                      className="text-sm text-whatsapp-600 hover:text-whatsapp-800 underline transition-colors"
                    >
                      Try different email
                    </button>
                  </div>
                </>
              ) : (
                /* OTP Form */
                <div className="space-y-6">
                  <button
                    onClick={() => setShowOtpForm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    ← Back to options
                  </button>

                  <div className="bg-whatsapp-50 border border-whatsapp-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <EnvelopeIcon className="h-5 w-5 text-whatsapp-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-whatsapp-700">
                        <p className="font-medium">Enter your 6-digit code</p>
                        <p className="mt-1">Check your email for the verification code.</p>
                      </div>
                    </div>
                  </div>

                  <OTPInput
                    length={6}
                    value={otpCode}
                    onChange={handleOtpChange}
                    onComplete={handleOtpComplete}
                    disabled={otpLoading}
                    error={otpError}
                    autoFocus={true}
                  />

                  {/* Resend OTP */}
                  <div className="text-center">
                    {canResend ? (
                      <button
                        onClick={handleResendOtp}
                        disabled={resendLoading}
                        className="inline-flex items-center text-sm text-whatsapp-600 hover:text-whatsapp-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendLoading ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1" />
                            Resend Code
                          </>
                        )}
                      </button>
                    ) : resendCountdown > 0 ? (
                      <p className="text-sm text-gray-500">
                        Resend available in {Math.floor(resendCountdown / 60)}:{(resendCountdown % 60).toString().padStart(2, '0')}
                      </p>
                    ) : null}
                  </div>

                  {/* Alternative Option */}
                  <div className="text-center pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">
                      Prefer the magic link?
                    </p>
                    <button
                      onClick={() => setShowOtpForm(false)}
                      className="text-sm text-whatsapp-600 hover:text-whatsapp-700 transition-colors"
                    >
                      Check your email for the login link instead
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Email Form */
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 focus:z-10 transition-colors"
                  placeholder="Enter your email"
                />
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-whatsapp-500 to-green-600 hover:from-whatsapp-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Login Link...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 mr-2" />
                      Send Login Link
                    </div>
                  )}
                </button>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-medium text-whatsapp-600 hover:text-whatsapp-500 transition-colors">
                    Sign up here
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Powered by Snwolley Business API Platform
          </p>
          <p className="text-xs text-gray-400 mt-1">
            © 2025 Snwolley. All rights reserved.
          </p>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-whatsapp-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 -right-20 w-60 h-60 bg-green-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-emerald-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </div>
    </div>
  );
};

export default Login;
